import { FC, useContext, useEffect, useState } from 'react';
import { AppLogoHeader } from 'templates/AppLogoHeader';
import { PageContent } from 'templates/PageContent';
import { GetPageProps, UserRegistrationState } from 'hooks/u002/useUserRegistrationHooks';
import { Form } from 'templates/Form';
import { Button } from 'atoms/Button';
import LoadingModal from 'organisms/LoadingModal';
import MessageModal from 'organisms/MessageModal';
import { FieldErrorMessages } from 'molecules/FieldErrorMessages';
import { CreateGroupFormSchemaType } from 'commons/auditionForm';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { useLocation, useNavigate } from 'react-router-dom';
import { PATHS } from 'commons/paths';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppContext } from 'templates/AppContextProvider';
import { UnauthAppFooter } from 'templates/UnauthAppFooter';
import { GetAuditionResponse } from 'hooks/u002/useGetAuditionHooks';
import { useCreateGroupSchema } from 'hooks/u002/useGroupFormSchema';
import { useCreateGroupUser } from 'hooks/u002/useCreateGroupUserHooks';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { GroupApplicationCompletion } from './GroupApplicationCompletion';
import { SelectCountryNumber } from 'atoms/SelectCountryNumber';
import PhoneInput, { Country } from 'react-phone-number-input/input';
import { E164Number, getCountryCallingCode } from 'libphonenumber-js';
import { FormSectionTitle } from 'atoms/FormSectionTitle';
import { LabeledInputField } from 'molecules/LabeledInputField';

type GroupRegistrationProps = GetPageProps<UserRegistrationState, 'GroupRegistration'> & {
  audition: GetAuditionResponse & {
    audition_id: string;
  };
  audition_revision: string;
};
export const GroupRegistration: FC<GroupRegistrationProps> = (props) => {
  const { t: tAuditionForm } = useTranslationWithLang('auditionForm');
  const navigate = useNavigate();
  const { isGlobal } = useContext(AppContext);
  const [countryCode, setCountryCode] = useState<Country>(isGlobal ? 'US' : 'JP');
  const [createGroupUser, isLoadingCreateGroupUser] = useCreateGroupUser();
  const CreateGroupFormSchema = useCreateGroupSchema();
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const representativeUser = queryParams.get('representative');
  const { currentLang } = useContext(AppContext);

  const { watch } = props;
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    clearErrors,
  } = useForm<CreateGroupFormSchemaType>({
    resolver: zodResolver(CreateGroupFormSchema),
    shouldFocusError: true,
    defaultValues: {
      name: '',
      phoneNumber: '',
      birthday: watch('birthday'),
    },
  });
  useEffect(() => {
    clearErrors();
  }, [currentLang, clearErrors]);

  const [ErrorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState<string[]>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const onSubmit: SubmitHandler<CreateGroupFormSchemaType> = async (form) => {
    setIsLoading(true);
    await createGroupUser({
      request: {
        name: form.name,
        birth: form.birthday,
        phone_number: form.phoneNumber,
        auditionId: props.audition.audition_id,
        audition_id: props.audition.audition_id,
        representative_user: representativeUser!,
        audition_revision: props.audition_revision,
      },
      onSuccess: () => {
        setIsLoading(false);
        setInfoMessage([tAuditionForm('TU002_069'), tAuditionForm('TU002_070')]);
        setIsSubmitted(true);
      },
      onError: (error) => {
        console.log({ error });
        if (error instanceof Error) {
          setErrorMessage(
            error.message === 'MM002F0093E'
              ? tAuditionForm('TU002_049')
              : error.message === 'MM002F0108E'
                ? tAuditionForm('TU002_060')
                : tAuditionForm('TU002_050')
          );
          setShowErrorModal(true);
          setIsLoading(false);
        }
      },
    });
  };
  if (isSubmitted) {
    // グループ応募完了画面
    return <GroupApplicationCompletion />;
  }

  return (
    <>
      {(isLoading || isLoadingCreateGroupUser) && <LoadingModal />}
      {infoMessage.length > 0 && (
        <MessageModal
          message={infoMessage}
          closeLabel={tAuditionForm('TU002_071')}
          onClose={() => {
            const toParam = new URLSearchParams();
            toParam.set('redirect', window.location.pathname);
            const to = PATHS.LOGIN + '?' + toParam.toString();
            navigate(to);
          }}
        />
      )}
      {showErrorModal && (
        <MessageModal
          message={ErrorMessage}
          closeLabel={tAuditionForm('TU002_032')}
          onClose={() => setShowErrorModal(false)}
        />
      )}
      <AppLogoHeader />
      <PageContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Form title={tAuditionForm('TU002_114')}>
            <div>
              <LabeledInputField
                title={tAuditionForm('TU002_099')}
                placeholder={tAuditionForm('TU002_021') + ' ' + tAuditionForm('TU002_022')}
                className="w-[25rem] my-4"
                ariaLabel={tAuditionForm('TU002_099')}
                {...register('name')}
                autoComplete="name"
                id="name"
              />
              <FieldErrorMessages messages={[errors.name?.message]} />
            </div>
            {!isGlobal && (
              <div>
                <FormSectionTitle text={tAuditionForm('TU002_012')} htmlFor="phoneNumber" />
                <div className="flex w-full">
                  <Controller
                    name="phoneNumber"
                    control={control}
                    render={({ field }) => (
                      <PhoneInput
                        country={countryCode as Country}
                        className="border-[--c-textinput-border] text-[--c-textinput-text] bg-[--c-textinput-background] rounded-[8px] h-[40px] px-2 border-[1px] mt-2"
                        id="phoneNumber"
                        name="phoneNumber"
                        placeholder={'08012345678'}
                        value={field.value || ''}
                        onChange={(e: E164Number) => {
                          field.onChange(e);
                          setValue('phoneNumber', e);
                        }}
                        control={control}
                      />
                    )}
                  />
                </div>
              </div>
            )}
            {isGlobal && (
              <div className="flex flex-col w-full mx-auto my-4">
                <FormSectionTitle text={tAuditionForm('TU002_012')} htmlFor={'phoneNumber'} />
                <div className="flex w-full max-w-md my-4">
                  <SelectCountryNumber
                    countryCode={countryCode}
                    setCountryCode={(code) => {
                      setCountryCode(code);
                      // 国選択後に既存の入力値があれば、国番号を更新
                      const phoneNumberValue = document.getElementById(
                        'phoneNumber'
                      ) as HTMLInputElement;
                      if (phoneNumberValue && phoneNumberValue.value) {
                        const countryCallingCode = getCountryCallingCode(code as Country);
                        const formattedValue = phoneNumberValue.value
                          ? `+${countryCallingCode}${phoneNumberValue.value}`
                          : '';
                        setValue('phoneNumber', formattedValue, { shouldValidate: true });
                      }
                    }}
                    className="w-32 text-[--c-textinput-text] bg-[--c-textinput-background] mr-2"
                  />
                  <Controller
                    name="phoneNumber"
                    control={control}
                    render={({ field }) => (
                      <PhoneInput
                        country={countryCode as Country}
                        className="w-full border-[--c-textinput-border] text-[--c-textinput-text] bg-[--c-textinput-background] rounded-[8px] h-[40px] ml-2 px-2 border-[1px]"
                        id="phoneNumber"
                        name="phoneNumber"
                        placeholder={'08012345678'}
                        value={field.value || ''}
                        onChange={(e: E164Number) => {
                          field.onChange(e);
                          setValue('phoneNumber', e);
                        }}
                        control={control}
                      />
                    )}
                  />
                </div>
                <FieldErrorMessages messages={[errors.phoneNumber?.message]} />
              </div>
            )}
            <div className={'h-[20px]'} />
            <div className={'flex justify-center items-center'}>
              <Button className="w-[10rem]" label={tAuditionForm('TU002_058')} type="submit" />
            </div>
          </Form>
        </form>
      </PageContent>
      <UnauthAppFooter />
    </>
  );
};
